'use client';

import { useTranslations } from 'next-intl';
import { ShieldCheck } from 'lucide-react';
import { type ReactElement, useContext, useLayoutEffect, useState } from 'react';
import { PropertyCard, Tooltip, TooltipRootContext, TriggerText, UnstyledButton } from '@zillow/constellation-10';
import { LucideIcon } from '@/src/components/LucideIcon';
import { verifiedInline, verifiedPhotoBadgeContent, verifiedPhotoBadgeTrigger } from './verifiedInline.styles';

const TOOLTIP_VIEWPORT_PADDING = 16;
const TRANSLATE_PATTERN = /^translate\(([-.\d]+)px, ([-.\d]+)px\)$/;

// Constellation's Tooltip has no viewport-containment (floating-ui `shift`) middleware, so it
// can render past the screen edge on mobile — see CDS-6339: https://zillowgroup.atlassian.net/browse/CDS-6339?focusedCommentId=6865629
// Until that's fixed upstream, this reads floating-ui's computed position from `TooltipRootContext`
// (`tooltip.floatingStyles`) and nudges it back into the viewport ourselves. We read from context
// rather than `getBoundingClientRect()` on our own prior transform because that self-referential
// read is fragile — real browsers reflect a transform they just applied, jsdom doesn't, so tests
// looped indefinitely (CDS-6372-adjacent Constellation upgrade fallout).
function useTooltipViewportShift(positioner: HTMLDivElement | null, arrow: SVGSVGElement | null) {
  const { tooltip } = useContext(TooltipRootContext);
  const baseTransform = tooltip.floatingStyles.transform;

  useLayoutEffect(() => {
    if (!positioner || !arrow || typeof baseTransform !== 'string') return;

    const match = baseTransform.match(TRANSLATE_PATTERN);
    if (!match) return;
    const baseX = Number(match[1]);
    const baseY = Number(match[2]);

    const updateShift = () => {
      const viewport = window.visualViewport;
      const viewportLeft = viewport?.offsetLeft ?? 0;
      const viewportWidth = viewport?.width ?? document.documentElement.clientWidth;
      const width = positioner.getBoundingClientRect().width;
      const leftBoundary = viewportLeft + TOOLTIP_VIEWPORT_PADDING;
      const rightBoundary = viewportLeft + viewportWidth - TOOLTIP_VIEWPORT_PADDING;
      const currentShift =
        baseX < leftBoundary
          ? leftBoundary - baseX
          : baseX + width > rightBoundary
            ? rightBoundary - (baseX + width)
            : 0;
      const adjustedTransform = `translate(${baseX + currentShift}px, ${baseY}px)`;
      const arrowTranslate = `${-currentShift}px 0px`;

      if (positioner.style.transform !== adjustedTransform) positioner.style.transform = adjustedTransform;
      if (arrow.style.translate !== arrowTranslate) arrow.style.translate = arrowTranslate;
    };

    updateShift();

    const resizeObserver = new ResizeObserver(updateShift);
    resizeObserver.observe(positioner);
    window.addEventListener('resize', updateShift);
    window.visualViewport?.addEventListener('resize', updateShift);
    window.visualViewport?.addEventListener('scroll', updateShift);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateShift);
      window.visualViewport?.removeEventListener('resize', updateShift);
      window.visualViewport?.removeEventListener('scroll', updateShift);
    };
  }, [arrow, positioner, baseTransform]);
}

type VerifiedInlineProps = {
  /** e.g. a card's `stopCardNavigation`, so tapping the label doesn't trigger navigation. */
  onLabelClick?: (e: React.MouseEvent) => void;
  labelKey?: 'verified' | 'verifiedListing';
  'data-testid'?: string;
};

type VerifiedTooltipProps = {
  children: ReactElement;
};

// `useTooltipViewportShift` reads `TooltipRootContext`, which only reaches descendants
// of `Tooltip.Root` — so it has to run from a component rendered inside it, not from
// `VerifiedTooltip` itself (which renders `Tooltip.Root`, and sits above it in the tree).
function TooltipViewportShiftEffect({
  positioner,
  arrow,
}: {
  positioner: HTMLDivElement | null;
  arrow: SVGSVGElement | null;
}) {
  useTooltipViewportShift(positioner, arrow);
  return null;
}

function VerifiedTooltip({ children }: VerifiedTooltipProps) {
  const t = useTranslations('hdp');
  const [tooltipPositioner, setTooltipPositioner] = useState<HTMLDivElement | null>(null);
  const [tooltipArrow, setTooltipArrow] = useState<SVGSVGElement | null>(null);

  return (
    <Tooltip.Root placement="top" shouldCloseOnViewportLeave>
      <TooltipViewportShiftEffect positioner={tooltipPositioner} arrow={tooltipArrow} />
      <Tooltip.Trigger>{children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          ref={setTooltipPositioner}
          style={{ maxInlineSize: `min(360px, calc(100dvw - ${TOOLTIP_VIEWPORT_PADDING * 2}px))` }}
        >
          {t('verifiedTooltip')}
          <Tooltip.Arrow ref={setTooltipArrow} />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

/**
 * Inline "Verified" indicator (verified icon + tooltip-triggering label) that
 * sits at the end of the HDP's `ListingSummaryModule` meta row.
 */
export function VerifiedInline({ onLabelClick, labelKey = 'verified', 'data-testid': testId }: VerifiedInlineProps) {
  const t = useTranslations('hdp');

  return (
    <span className={verifiedInline} data-testid={testId}>
      {/* eslint-disable-next-line hotpads/no-magic-numbers -- 1em matches the font-size exactly; inline style beats Constellation's Panda atomic size class */}
      <LucideIcon icon={ShieldCheck} size="sm" style={{ width: '1em', height: '1em' }} />
      <VerifiedTooltip>
        <TriggerText css={{ color: 'inherit' }} onClick={onLabelClick}>
          {t(labelKey)}
        </TriggerText>
      </VerifiedTooltip>
    </span>
  );
}

export function VerifiedPhotoBadge({ onLabelClick, 'data-testid': testId }: VerifiedInlineProps) {
  const t = useTranslations('hdp');

  return (
    <VerifiedTooltip>
      <UnstyledButton css={verifiedPhotoBadgeTrigger} onClick={onLabelClick} data-testid={testId}>
        <PropertyCard.Badge tone="neutral">
          <span className={verifiedPhotoBadgeContent}>
            <LucideIcon icon={ShieldCheck} size="sm" />
            {t('verified')}
          </span>
        </PropertyCard.Badge>
      </UnstyledButton>
    </VerifiedTooltip>
  );
}
